/**
 * Worth adding: the captured offers Wallet recommends, per issuer. The server
 * decides which and why (`GET /api/offers/capture/recommended`, over the
 * interests file); this module only fetches, files and words them.
 */

import { authedFetch } from "./api";
import { orThrow } from "./http";
import type { AddState, AddedOffer } from "./offerAdds";
import { daysBetween } from "./offersPage";

/** One card an offer is on, with that card's own row in the catalog. */
export interface OfferCard {
  card_id: number;
  capture_key: string;
  expires_on: string | null;
  first_seen_on: string;
  dismissed: boolean;
  /** Add on next run, asked for on this card. */
  flagged: boolean;
  /** What the last run that pressed Add here found; null when none has. */
  add_state: AddState | null;
  /** Runs that pressed Add and read it still not added. */
  add_failures: number;
}

/** One offer as its issuer lists it, once however many cards carry it. */
export interface RecommendedOffer {
  merchant: string;
  title: string | null;
  /** The spend and the reward as one sentence, when the issuer states them. */
  terms: string | null;
  /** The issuer's own how-to-use text, terms and cap; null until captured. */
  details?: string | null;
  fine_print?: string | null;
  max_reward?: number | null;
  /** The earliest end date across its cards. */
  expires_on: string | null;
  first_seen_on: string;
  /** Worth adding, by the interests file; the rest show under All offers. */
  worth: boolean;
  /** Why it is recommended: listed brands first, then "free" / "travel deal". */
  matched: string[];
  great_deal: boolean;
  /** Not interested on any of its cards. */
  dismissed: boolean;
  /** Add on next run, on any of its cards. */
  flagged: boolean;
  /** A card gave up adding it: it did not take twice. */
  needs_look: boolean;
  /** The last run pressed Add and the card's read did not show it added. */
  unconfirmed: boolean;
  /** The cards it is open on, not yet added. */
  cards: OfferCard[];
  /** The issuer's other cards it is already added on. Absent from an older server. */
  added_on?: string[];
}

export interface RecommendedCard {
  card_id: number;
  card_name: string;
  issuer: string;
  source: string;
  url: string;
  last_read_on: string | null;
  read_complete: boolean;
}

export interface RecommendedIssuer {
  issuer: string;
  source: string;
  cards: RecommendedCard[];
  offers: RecommendedOffer[];
  /** Whether a run can add this issuer's offers, and whether it does. */
  auto_add: { supported: boolean; enabled: boolean };
  /** What the issuer's last read run added, plus every pinned add. */
  added_last_run: AddedOffer[];
  /** What the next run presses and where, from the plan's own rule. Empty
   * unless the issuer adds for you; absent from an older server. */
  next_run?: NextRunAdd[];
}

/** One press the next run makes: an offer's catalog key on one card. */
export interface NextRunAdd {
  key: string;
  card_id: number;
  card_name: string;
  /** Why this card, for an issuer that adds on one card only: the rule's
   * category code, "brand", "default", "first" or "retry". */
  reason?: string;
}

export interface Recommended {
  today: string;
  issuers: RecommendedIssuer[];
  interests_configured: boolean;
}

/** An offer's identity on the page: its first card's catalog row. */
export const offerKey = (offer: RecommendedOffer): string => offer.cards[0]?.capture_key ?? offer.merchant;

export type WorthFilter = "all" | "brand" | "deal";
export type OfferTab = "added" | "needs" | "next" | "all" | "soon";

/** The planned presses that belong to this offer: one per card it goes on. */
export const addsFor = (offer: RecommendedOffer, adds: NextRunAdd[]): NextRunAdd[] =>
  adds.filter((a) => offer.cards.some((c) => c.capture_key === a.key));

/** Why the next run puts an offer on this card, as a sentence. */
export function reasonText(reason: string | undefined, card: string): string | null {
  if (!reason) return null;
  if (reason === "retry") return `Did not take last run, so tried again on ${card}`;
  if (reason === "default") return `Default card, so ${card}`;
  if (reason === "brand") return `Listed brand, so ${card}`;
  if (reason === "first") return `Only ${card} lists it`;
  const word = reason.toLocaleLowerCase();
  return `${word.charAt(0).toLocaleUpperCase()}${word.slice(1)}, so ${card}`;
}

/** Ends within `soonDays` of today, counting today. */
export function endsSoon(offer: RecommendedOffer, today: string, soonDays: number): boolean {
  const left = daysBetween(today, offer.expires_on);
  return left !== null && left >= 0 && left <= soonDays;
}

const URL = "/api/offers/capture/recommended";

export async function fetchRecommended(): Promise<Recommended> {
  const res = await authedFetch(URL, { cache: "no-store" });
  return await orThrow(res, "Could not load offers worth adding") as Recommended;
}

export async function setDismissed(captureKey: string, dismissed: boolean): Promise<void> {
  const res = await authedFetch(`${URL}/${captureKey}/dismissed`, {
    method: dismissed ? "PUT" : "DELETE",
  });
  await orThrow(res, dismissed ? "Could not hide that offer" : "Could not show that offer again");
}

export async function setFlagged(captureKey: string, flagged: boolean): Promise<void> {
  const res = await authedFetch(`${URL}/${captureKey}/flagged`, {
    method: flagged ? "PUT" : "DELETE",
  });
  await orThrow(res, flagged ? "Could not ask the next run to add that offer" : "Could not undo that");
}

/** The server's great-deal reasons; every other matched reason is a brand. */
export const DEAL_REASONS: ReadonlySet<string> = new Set(["free", "travel deal"]);

/** A brand match is anything matched besides the great-deal reasons. */
export function matchesFilter(offer: RecommendedOffer, filter: WorthFilter): boolean {
  if (filter === "all") return true;
  const brand = offer.matched.some((m) => !DEAL_REASONS.has(m));
  return filter === "brand" ? brand : offer.great_deal;
}

/** After this many days a card's read is called out as possibly out of date. */
export const STALE_READ_DAYS = 7;

export function readFreshness(card: RecommendedCard, today: string): { text: string; stale: boolean } {
  if (!card.last_read_on) return { text: "never read", stale: true };
  const days = daysBetween(card.last_read_on, today) ?? 0;
  const when = days <= 0 ? "read today" : days === 1 ? "read yesterday" : `read ${days} days ago`;
  const partial = card.read_complete ? "" : ", partly";
  return { text: when + partial, stale: days >= STALE_READ_DAYS || !card.read_complete };
}

/** An issuer's read coverage: one line when every card was read alike, else one per card. */
export function readCoverage(cards: RecommendedCard[], today: string): { text: string; stale: boolean }[] {
  const each = cards.map((card) => readFreshness(card, today));
  if (each.length > 0 && each.every((f) => f.text === each[0].text)) return [each[0]];
  return each.map((f, i) => ({ ...f, text: `${cards[i].card_name} ${f.text}` }));
}
